import React from 'react';
import PropTypes from 'prop-types';
import {history} from 'client/history';
import classnames from 'classnames';
import moment from 'moment';
import download from 'downloadjs';
import sanitize from 'lib/sanitize';
import WordTooltip from 'sg/WordTooltip/WordTooltip.react';
import stats, {BUCKETS_ARRAY_INCREMENTING} from 'lib/StatsUtil';
import AssignmentCompletionStatus from 'client/Classrooms/shared/AssignmentCompletionStatus/AssignmentCompletionStatus.react';
import {humanReadableDurationString, renderMoment} from 'lib/timeUtils';
import classroomV2Store from 'client/Classrooms/Classroom/ClassroomV2.store';
import {AssignmentModelV3} from 'resources/augmented/Assignment/AssignmentModel.v3';
import {SectionModelV1} from 'resources/augmented/Section/SectionModel.v1';
import appStore from 'client/AppStore';
import TeacherPracticeExamOptionsDropdown from 'client/Classrooms/shared/TeacherPracticeExamOptionsDropdown/TeacherPracticeExamOptionsDropdown.react';
import {addGenericErrorToast, addToast, Button} from '@albert-io/atomic';

import assignmentV2ResultsUtils from '../assignmentV2Results.utils';

import './assignment-results-details.scss';

export default class AssignmentResultsDetails extends React.Component {
  static propTypes = {
    assignment: PropTypes.instanceOf(AssignmentModelV3),
    activeSection: PropTypes.instanceOf(SectionModelV1)
  };

  downloadCSV = () => {
    const {assignment, activeSection} = this.props;
    const dateString = moment().format('YYYY-MM-DD');
    const studentCSV = assignmentV2ResultsUtils.generateStudentCSVData(activeSection, assignment);
    const questionCSV = assignmentV2ResultsUtils.generateQuestionCSVData(activeSection);
    const classroomName = classroomV2Store.getActiveClassroom().getName();
    const assignmentName = assignment.getName();
    const sectionTitle = activeSection.getTitle();
    const makeFileName = (type) => {
      // eslint-disable-next-line max-len
      return `${sanitize.filename(
        `${classroomName}-${assignmentName}-${sectionTitle}-Assignment-${type}-Summary-${dateString}`
      )}.csv`;
    };
    download(studentCSV, makeFileName('Student'), 'text/csv');
    setTimeout(() => {
      download(questionCSV, makeFileName('Question'), 'text/csv');
    }, 0);
  };

  onDeleteModalConfirm = () => {
    this.props.assignment
      .delete()
      .then(() => {
        addToast({
          type: 'success',
          title: 'Success!',
          message: 'Your assessment was successfully deleted'
        });
        history.replaceState(
          null,
          `/classes/${classroomV2Store.getActiveClassroom().getId()}/exams`
        );
      })
      .catch((e) => {
        logger.error('Failed deleting assessment: %s', e.message || 'unknown error');
        addGenericErrorToast();
      });
  };

  getTopBar(assignment, activeSection) {
    const classroomId = classroomV2Store.getActiveClassroom().getId();
    const shouldShowExportButton =
      !assignment.getStartDate() || assignment.getStartDate().isBefore(moment());

    const exportCSVButton = (
      <Button color='secondary' size='s' onClick={this.downloadCSV}>
        Export CSV
      </Button>
    );

    return (
      <div className='assignment-results-details__top-bar'>
        <div className='assignment-results-details__heading-wrapper'>
          <h1 className='assignment-results-details__heading'>{assignment.getName()}</h1>
          <h2 className='assignment-results-details__subheading'>{activeSection.getTitle()}</h2>
        </div>
        <div className='assignment-results-details__actions-wrapper'>
          {shouldShowExportButton && exportCSVButton}
          <TeacherPracticeExamOptionsDropdown
            onEditRoute={`${appStore.routerProps.location.pathname}/edit`}
            exam={assignment}
            onDelete={this.onDeleteModalConfirm}
            classroomId={classroomId}
          />
        </div>
      </div>
    );
  }

  render() {
    const {assignment, activeSection} = this.props;
    return (
      <div className='card assignment-results-details'>
        {this.getTopBar(assignment, activeSection)}
        <MasteryLevelTooltip />
        <AssignmentResultsMasteryBreakdown assignment={assignment} activeSection={activeSection} />
        <AssignmentResultsDataPointsList assignment={assignment} activeSection={activeSection} />
      </div>
    );
  }
}

class MasteryLevelTooltip extends React.PureComponent {
  render() {
    return (
      <div className='mastery-level-tooltip'>
        <WordTooltip
          term={
            <span>
              Mastery Levels <span className='fa fa-question-circle' />
            </span>
          }
          content={
            <ul className='mastery-level-tooltip__list'>
              {BUCKETS_ARRAY_INCREMENTING.map((bucket, i) => (
                <li className='mastery-level-tooltip__item' key={i}>
                  {bucket.DISPLAY_NAME}: {bucket.MIN}-{bucket.MAX}
                </li>
              ))}
            </ul>
          }
        />
      </div>
    );
  }
}

class AssignmentResultsMasteryBreakdown extends React.PureComponent {
  static propTypes = {
    activeSection: PropTypes.instanceOf(SectionModelV1)
  };

  makeStudents(studentsByMastery) {
    return BUCKETS_ARRAY_INCREMENTING.map((bucket, i) => {
      return (
        <AssignmentResultsMasteryItem
          key={i}
          itemCount={studentsByMastery.get(bucket.BUCKET_NAME).size}
          totalCount={this.props.activeSection.getStudents().size}
          label={bucket.DISPLAY_NAME}
        />
      );
    });
  }

  render() {
    const studentsByMastery = this.props.activeSection.getStudentsByMasteryBuckets();
    return (
      <ul className='assignment-results-mastery-breakdown'>
        <AssignmentResultsMasteryItem
          itemCount={studentsByMastery.get('NOT_AVAILABLE').size}
          totalCount={this.props.activeSection.getStudents().size}
          label='N/A'
        />
        {this.makeStudents(studentsByMastery)}
      </ul>
    );
  }
}

class AssignmentResultsMasteryItem extends React.PureComponent {
  static propTypes = {
    itemCount: PropTypes.number,
    totalCount: PropTypes.number,
    label: PropTypes.string
  };

  render() {
    const {itemCount, totalCount, label} = this.props;
    const percentage = `${stats.percentage(itemCount, totalCount)}%`;
    return (
      <li className='assignment-results-mastery-item'>
        {percentage}
        <div className='assignment-results-mastery-item__chart'>
          <div
            className={classnames(
              'assignment-results-mastery-item__chart-fill',
              `assignment-results-mastery-item__chart-fill--${label
                .replace(/[^\w]/, '')
                .toLowerCase()}`
            )}
            style={{height: percentage}}
          />
        </div>
        {label}
      </li>
    );
  }
}

class AssignmentResultsDataPointsList extends React.PureComponent {
  static propTypes = {
    assignment: PropTypes.instanceOf(AssignmentModelV3),
    activeSection: PropTypes.instanceOf(SectionModelV1)
  };

  render() {
    const {assignment, activeSection} = this.props;
    const totalSubmitted =
      activeSection.getCountOfOnTimeSubmittedStudents() +
      activeSection.getCountOfLateSubmittedStudents();
    const timeLimit = activeSection.getTimeLimit();
    return (
      <ul className='assignment-results-data-points-list'>
        <AssignmentResultsDataPointItem
          label='Progress'
          content={
            <div className='assignment-progress-data-point'>
              <div className='assignment-progress-data-point__label'>
                {totalSubmitted} / {activeSection.getStudents().size} submitted
              </div>
              <AssignmentCompletionStatus
                inline
                // This is actually the number submitted on time
                numberSubmitted={activeSection.getCountOfOnTimeSubmittedStudents()}
                numberSubmittedLate={activeSection.getCountOfLateSubmittedStudents()}
                numberNotSubmitted={activeSection.getCountOfNotSubmittedStudents()}
              />
            </div>
          }
        />
        <AssignmentResultsDataPointItem
          label='Avg Grade'
          content={`${activeSection.getAverageGradeOfSubmittedStudents()}%`}
        />
        <AssignmentResultsDataPointItem
          label='Avg Time Spent'
          content={humanReadableDurationString(
            activeSection.getAverageTimeSpentOfSubmittedStudents()
          )}
        />
        <AssignmentResultsDataPointItem
          label='Due'
          content={renderMoment(assignment.getDueDate(), 'dateFormalShort')}
          detail={`@ ${renderMoment(assignment.getDueDate(), 'time')}`}
        />
        <AssignmentResultsDataPointItem
          label='Content'
          content={`${activeSection.getMeta().getCountOfQuestions()}`}
          detail='Questions'
        />
        <AssignmentResultsDataPointItem
          label='Time Limit'
          content={timeLimit ? `${timeLimit} min` : 'No Limit'}
          detail='Continuous timer'
        />
      </ul>
    );
  }
}

class AssignmentResultsDataPointItem extends React.PureComponent {
  static propTypes = {
    label: PropTypes.string,
    content: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    detail: PropTypes.string
  };

  render() {
    const content =
      typeof this.props.content === 'string' ? (
        <div className='assignment-results-data-point-item__content'>{this.props.content}</div>
      ) : (
        this.props.content
      );
    const detail = this.props.detail ? (
      <div className='assignment-results-data-point-item__detail'>{this.props.detail}</div>
    ) : null;
    return (
      <li className='assignment-results-data-point-item'>
        <div className='assignment-results-data-point-item__content-wrapper'>
          <div className='assignment-results-data-point-item__label'>{this.props.label}:</div>
          {content}
          {detail}
        </div>
      </li>
    );
  }
}
